/**
 * Retro UI sound effects for the PS1 skin.
 *
 * Every sound is synthesised with Web Audio — no files to ship or license. The
 * set was picked by Pipe from `design-mockups/ps1/sounds/index.html` (the codes
 * in the comments refer to that page). The context is created lazily on the
 * first sound and resumed on the first user gesture, so merely opening the app
 * does no audio work, and the browser autoplay policy is respected.
 *
 * The app stays open for days, and an `AudioContext` does not survive that on
 * its own. WebKit parks it in the non-standard `interrupted` state after sleep
 * or an output device change, and can leave it reporting `running` with a clock
 * that no longer moves. {@link healthyContext} and {@link unlockSfx} recover from
 * both, replacing the context when it cannot be resumed. A context can also
 * keep running, clock and all, while nothing reaches the speakers (an output
 * that went away under it), which no state shows; so the first gesture after a
 * quiet spell starts a fresh context rather than trusting the old one. What
 * happened to each context is kept in {@link SFX_LOG_KEY} for diagnosing.
 *
 * None of that was enough: WebKit can also lose audio for the whole process,
 * with every context reporting `running` and a moving clock, and only a
 * relaunch brings it back (WebKit bugs 276687, 291892). So in the desktop app
 * each sound is rendered once with an `OfflineAudioContext` and handed to the
 * Rust shell, which plays it through macOS system sound services, outside
 * WebKit. Live Web Audio remains the path in a browser, on other platforms, and
 * until the clips are loaded.
 */

import { isTauri } from "./isTauri";

export type Sfx =
  | "hover"
  | "confirm"
  | "back"
  | "error"
  | "toggleOn"
  | "toggleOff"
  | "open"
  | "done"
  | "delete"
  | "levelup"
  | "notify"
  | "streak"
  | "key";

export const SOUND_STORAGE_KEY = "busee.sound";
export const SOUND_SETTINGS_EVENT = "busee:sound-settings";

const VOLUME = 0.5;
/** A running context whose clock did not move across this much wall time is dead. */
const STALL_AFTER_MS = 1000;
/** How long a resume may take before the next gesture replaces the context. */
const RESUME_GRACE_MS = 800;
/** A sound waiting on a resume is dropped after this: late feedback is worse than none. */
const STALE_AFTER_MS = 600;
/** Longer than the longest voice (levelup, 1.3 s). */
const BUS_LIFETIME_MS = 2000;
/** After this long without a sound, the next gesture plays on a fresh context. */
const IDLE_RECYCLE_MS = 90_000;
/** Recent context events kept on the device, newest last. */
export const SFX_LOG_KEY = "busee.sfx.log";
const LOG_LIMIT = 40;

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let clock: { context: number; wall: number } | null = null;
let wedged = false;
let watching = false;
/** Wall time (it keeps counting through sleep) of the last sound, or of the context's start. */
let lastSoundAt = 0;

function note(event: string): void {
  const store = storage();
  if (!store) {
    return;
  }
  try {
    const log = JSON.parse(store.getItem(SFX_LOG_KEY) ?? "[]") as string[];
    log.push(`${new Date().toISOString()} ${event}`);
    store.setItem(SFX_LOG_KEY, JSON.stringify(log.slice(-LOG_LIMIT)));
  } catch {
    // Diagnostics only.
  }
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function soundEnabled(): boolean {
  return storage()?.getItem(SOUND_STORAGE_KEY) !== "off";
}

export function setSoundEnabled(on: boolean): boolean {
  try {
    window.localStorage.setItem(SOUND_STORAGE_KEY, on ? "on" : "off");
  } catch {
    return false;
  }
  window.dispatchEvent(new Event(SOUND_SETTINGS_EVENT));
  return true;
}

function audio(): AudioContext | null {
  if (ctx) {
    return ctx;
  }
  const Ctor = typeof window === "undefined" ? undefined : (window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  if (!Ctor) {
    return null;
  }
  const created = new Ctor();
  ctx = created;
  lastSoundAt = Date.now();
  note(`new context (${created.state})`);
  created.onstatechange = () => note(`state ${created.state}${ctx === created ? "" : " (old)"}`);
  master = outputChain(created);
  clock = null;
  wedged = false;
  watchForWake();
  return ctx;
}

/** The master volume and console-DAC colour every voice plays through. */
function outputChain(c: BaseAudioContext): GainNode {
  const gain = c.createGain();
  gain.gain.value = VOLUME;
  // A gentle lowpass so square waves sound like an old console DAC, not a buzzer.
  const lowpass = c.createBiquadFilter();
  lowpass.type = "lowpass";
  lowpass.frequency.value = 7000;
  gain.connect(lowpass);
  lowpass.connect(c.destination);
  return gain;
}

function discardContext(reason: string): void {
  const old = ctx;
  note(`replace: ${reason}`);
  ctx = null;
  master = null;
  lastConfirm = null;
  void old?.close().catch(() => {});
}

function resume(context: AudioContext): void {
  void context.resume().catch(() => {});
  window.setTimeout(() => {
    if (ctx === context && context.state !== "running") {
      wedged = true;
      note(`resume did not take (${context.state})`);
    }
  }, RESUME_GRACE_MS);
}

/** Coming back from sleep or a hidden window is when a parked context can resume. */
function watchForWake(): void {
  if (watching || typeof window.addEventListener !== "function") {
    return;
  }
  watching = true;
  const wake = () => {
    if (ctx && ctx.state !== "running") {
      void ctx.resume().catch(() => {});
    }
  };
  window.addEventListener("focus", wake);
  window.addEventListener("pageshow", wake);
  document.addEventListener("visibilitychange", wake);
  // The context may keep "running" against an output that is gone.
  navigator.mediaDevices?.addEventListener?.("devicechange", () => {
    wedged = true;
    note("output devices changed");
  });
}

/**
 * The context to play on, replaced first when it is closed or stalled. One that
 * would not resume waits for a gesture: without one a new context is just as
 * blocked, so replacing it on every chime would only churn.
 */
function healthyContext(gesture: boolean): AudioContext | null {
  const current = ctx;
  if (current) {
    const now = performance.now();
    const stalled =
      current.state === "running" &&
      clock !== null &&
      now - clock.wall >= STALL_AFTER_MS &&
      current.currentTime - clock.context < 0.001;
    const idle = gesture && Date.now() - lastSoundAt >= IDLE_RECYCLE_MS;
    if (current.state === "closed") {
      discardContext("closed");
    } else if (stalled) {
      discardContext("clock stopped");
    } else if (wedged && gesture) {
      discardContext("would not resume");
    } else if (idle) {
      discardContext("quiet spell");
    }
  }
  const context = audio();
  if (context) {
    // Only when it moved: re-stamping a frozen clock would restart the wait on
    // every click, and quick clicks would never reach the stall check.
    if (context.state !== "running") {
      clock = null;
    } else if (!clock || context.currentTime > clock.context) {
      clock = { context: context.currentTime, wall: performance.now() };
    }
  }
  return context;
}

/** Call from a user gesture so later sounds (hover included) are allowed to play. */
export function unlockSfx(): void {
  startNative();
  if (native === "ready") {
    return;
  }
  const context = healthyContext(true);
  if (context && context.state !== "running") {
    resume(context);
  }
}
type ToneSpec = { type?: OscillatorType; f0: number; f1?: number; dur?: number; vol?: number; at?: number; vib?: number };

function tone(c: BaseAudioContext, out: AudioNode, { type = "square", f0, f1 = f0, dur = 0.08, vol = 0.25, at = 0, vib = 0 }: ToneSpec) {
  const t = c.currentTime + at;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(f0, t);
  if (f1 !== f0) {
    osc.frequency.exponentialRampToValueAtTime(f1, t + dur);
  }
  if (vib) {
    const lfo = c.createOscillator();
    const depth = c.createGain();
    lfo.frequency.value = 18;
    depth.gain.value = vib;
    lfo.connect(depth);
    depth.connect(osc.frequency);
    lfo.start(t);
    lfo.stop(t + dur + 0.02);
  }
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(vol, t + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(gain);
  gain.connect(out);
  osc.start(t);
  osc.stop(t + dur + 0.02);
}

type NoiseSpec = { dur?: number; vol?: number; at?: number; type?: BiquadFilterType; f0?: number; f1?: number; q?: number };

function noise(c: BaseAudioContext, out: AudioNode, { dur = 0.05, vol = 0.2, at = 0, type = "bandpass", f0 = 2000, f1 = f0, q = 1 }: NoiseSpec) {
  const t = c.currentTime + at;
  const buffer = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
  const data = buffer.getChannelData(0);
  // Sample-and-hold every 4 samples: crunchy, low-rate noise.
  for (let i = 0; i < data.length; i += 4) {
    const v = Math.random() * 2 - 1;
    for (let j = i; j < Math.min(i + 4, data.length); j += 1) data[j] = v;
  }
  const source = c.createBufferSource();
  source.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = type;
  filter.Q.value = q;
  filter.frequency.setValueAtTime(f0, t);
  if (f1 !== f0) {
    filter.frequency.exponentialRampToValueAtTime(f1, t + dur);
  }
  const gain = c.createGain();
  gain.gain.setValueAtTime(vol, t);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  source.connect(filter);
  filter.connect(gain);
  gain.connect(out);
  source.start(t);
}

const N = { C4: 262, C5: 523, E5: 659, G5: 784, A5: 880, B5: 988, C6: 1047, E6: 1319, G6: 1568, C7: 2093 };

type Voice = (c: BaseAudioContext, out: AudioNode) => void;
const arp = (c: BaseAudioContext, out: AudioNode, notes: number[], step: number, spec: Omit<ToneSpec, "f0" | "at">) =>
  notes.forEach((f0, i) => tone(c, out, { f0, at: i * step, ...spec }));

/** Task complete rotates through these three (D1 Item get, D2 Check!, D3 Sparkle). */
const DONE_VOICES: Voice[] = [
  (c, o) => arp(c, o, [N.C5, N.E5, N.G5, N.C6], 0.06, { dur: 0.1, vol: 0.13 }),
  (c, o) => {
    tone(c, o, { f0: N.G5, dur: 0.06, vol: 0.15 });
    tone(c, o, { f0: N.G6, at: 0.07, dur: 0.18, vol: 0.13 });
  },
  (c, o) => arp(c, o, [N.C6, N.E6, N.G6, N.C7, N.G6, N.C7], 0.035, { type: "triangle", dur: 0.06, vol: 0.18 }),
];

const VOICES: Record<Exclude<Sfx, "done">, Voice> = {
  // H5 Wood tap
  hover: (c, o) => noise(c, o, { dur: 0.03, vol: 0.35, f0: 1800, q: 4 }),
  // C2 PS confirm
  confirm: (c, o) => {
    tone(c, o, { type: "triangle", f0: N.C6, dur: 0.14, vol: 0.3 });
    tone(c, o, { type: "triangle", f0: N.G6, dur: 0.14, vol: 0.2 });
  },
  // B3 Thunk
  back: (c, o) => tone(c, o, { f0: 220, f1: 140, dur: 0.07, vol: 0.18 }),
  // E1 Buzz
  error: (c, o) => {
    tone(c, o, { f0: 110, dur: 0.08, vol: 0.2 });
    tone(c, o, { f0: 110, at: 0.11, dur: 0.1, vol: 0.2 });
  },
  // T2 Click pair
  toggleOn: (c, o) => noise(c, o, { dur: 0.025, vol: 0.4, f0: 3200, q: 3 }),
  toggleOff: (c, o) => noise(c, o, { dur: 0.025, vol: 0.4, f0: 1600, q: 3 }),
  // O4 Memory card
  open: (c, o) => {
    tone(c, o, { type: "sine", f0: N.E5, dur: 0.25, vol: 0.2 });
    tone(c, o, { type: "sine", f0: N.B5, at: 0.08, dur: 0.3, vol: 0.15 });
  },
  // D1 Crunch
  delete: (c, o) => noise(c, o, { dur: 0.15, vol: 0.4, type: "lowpass", f0: 3000, f1: 200 }),
  // L1 Fanfare
  levelup: (c, o) => {
    arp(c, o, [N.C5, N.C5, N.C5], 0.09, { dur: 0.08, vol: 0.14 });
    tone(c, o, { f0: N.C5, at: 0.3, dur: 0.15, vol: 0.14 });
    tone(c, o, { f0: N.E5, at: 0.45, dur: 0.15, vol: 0.14 });
    tone(c, o, { f0: N.G5, at: 0.6, dur: 0.15, vol: 0.14 });
    tone(c, o, { f0: N.C6, at: 0.75, dur: 0.5, vol: 0.14 });
    tone(c, o, { type: "triangle", f0: N.C4, at: 0.75, dur: 0.5, vol: 0.3 });
  },
  // N3 Alert
  notify: (c, o) => [0, 0.18, 0.36].forEach((at) => tone(c, o, { f0: 880, f1: 660, at, dur: 0.12, vol: 0.12 })),
  // S1 Flame
  streak: (c, o) => {
    noise(c, o, { dur: 0.25, vol: 0.2, f0: 600, f1: 2500, q: 1 });
    arp(c, o, [N.G5, N.C6], 0.08, { dur: 0.1, vol: 0.13 });
  },
  // T2 Keytick
  key: (c, o) => noise(c, o, { dur: 0.012, vol: 0.15, f0: 4000 + Math.random() * 1500, q: 3 }),
};

let lastDone: number | null = null;

/**
 * The generic click sound of the most recent press. A button whose action has
 * its own sound (delete, done, an error) fires both in the same tick; that
 * specific sound mutes the generic one instead of playing over it.
 */
let lastConfirm: { bus: GainNode; at: number } | null = null;
const SPECIFIC: ReadonlySet<Sfx> = new Set(["back", "delete", "done", "error", "levelup", "open"]);
const SUPERSEDE_WINDOW_S = 0.08;

/** The next completion voice: random, but never the same one twice in a row. */
export function nextDoneIndex(random = Math.random): number {
  let index = Math.floor(random() * DONE_VOICES.length);
  if (index === lastDone) {
    index = (index + 1) % DONE_VOICES.length;
  }
  lastDone = index;
  return index;
}

function render(c: AudioContext, name: Sfx): void {
  if (!master) {
    return;
  }
  lastSoundAt = Date.now();
  if (SPECIFIC.has(name) && lastConfirm && c.currentTime - lastConfirm.at < SUPERSEDE_WINDOW_S) {
    lastConfirm.bus.gain.value = 0;
    lastConfirm = null;
  }
  const bus = c.createGain();
  bus.connect(master);
  const voice = name === "done" ? DONE_VOICES[nextDoneIndex()]! : VOICES[name];
  voice(c, bus);
  if (name === "confirm") {
    lastConfirm = { bus, at: c.currentTime };
  }
  // Left connected, every sound ever played stays in the graph: thousands of
  // hovers over a long session is a real load on the audio thread.
  window.setTimeout(() => bus.disconnect(), BUS_LIFETIME_MS);
}

/** Play one effect. Sound is additive: any failure is swallowed. */
export function playSfx(name: Sfx): void {
  if (!soundEnabled()) {
    return;
  }
  startNative();
  if (native === "ready") {
    playNative(name);
    return;
  }
  try {
    const c = healthyContext(false);
    if (!c) {
      return;
    }
    if (c.state === "running") {
      render(c, name);
      return;
    }
    // Scheduled now, these would all fire together when the context wakes.
    const asked = performance.now();
    resume(c);
    if (name === "hover" || name === "key") {
      return;
    }
    void c
      .resume()
      .then(() => {
        if (ctx === c && c.state === "running" && performance.now() - asked < STALE_AFTER_MS) {
          render(c, name);
        }
      })
      .catch(() => {});
  } catch {
    // A missing output device or a policy refusal must never break a screen.
  }
}
/** Sounds with random parts get a few takes, rotated, so repeats do not sound canned. */
const TAKES: Partial<Record<Sfx, number>> = { hover: 4, key: 6, done: DONE_VOICES.length };
const RENDER_RATE = 44_100;
/** Longer than the longest voice; the silent tail is trimmed off. */
const RENDER_SECONDS = 1.5;

let native: "loading" | "ready" | "off" | null = null;
let invokeNative: ((cmd: string, args: Record<string, unknown>) => Promise<unknown>) | null = null;
const nextTake = new Map<Sfx, number>();
let pendingConfirm: number | null = null;

function clipName(name: Sfx, take: number): string {
  return `${name.toLowerCase()}-${take}`;
}

/** 16-bit mono PCM, cut after the last audible sample. */
function encodeWav(samples: Float32Array, rate: number): Uint8Array {
  let end = samples.length;
  while (end > 0 && Math.abs(samples[end - 1]!) < 1e-4) {
    end -= 1;
  }
  const length = Math.max(end, 1);
  const bytes = new Uint8Array(44 + length * 2);
  const view = new DataView(bytes.buffer);
  const text = (at: number, value: string) => [...value].forEach((ch, i) => view.setUint8(at + i, ch.charCodeAt(0)));
  text(0, "RIFF");
  view.setUint32(4, 36 + length * 2, true);
  text(8, "WAVE");
  text(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  text(36, "data");
  view.setUint32(40, length * 2, true);
  for (let i = 0; i < length; i += 1) {
    const v = Math.max(-1, Math.min(1, samples[i] ?? 0));
    view.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return bytes;
}

async function renderClip(voice: Voice): Promise<Uint8Array> {
  const c = new OfflineAudioContext(1, Math.ceil(RENDER_RATE * RENDER_SECONDS), RENDER_RATE);
  voice(c, outputChain(c));
  const buffer = await c.startRendering();
  return encodeWav(buffer.getChannelData(0), RENDER_RATE);
}

async function loadNative(): Promise<number> {
  const { invoke } = await import("@tauri-apps/api/core");
  const names = [...(Object.keys(VOICES) as Sfx[]), "done" as const];
  let loaded = 0;
  for (const name of names) {
    for (let take = 0; take < (TAKES[name] ?? 1); take += 1) {
      const wav = await renderClip(name === "done" ? DONE_VOICES[take]! : VOICES[name]);
      // A plain array: a Uint8Array would serialise as an object.
      await invoke("sfx_load", { name: clipName(name, take), wav: Array.from(wav) });
      loaded += 1;
    }
  }
  invokeNative = invoke;
  return loaded;
}

/** Start loading the clips into the desktop shell, once per page. */
function startNative(): void {
  if (native !== null) {
    return;
  }
  if (!isTauri() || typeof OfflineAudioContext === "undefined") {
    native = "off";
    return;
  }
  native = "loading";
  loadNative().then(
    (count) => {
      native = "ready";
      note(`native player ready (${count} clips)`);
    },
    (error: unknown) => {
      native = "off";
      note(`native player off: ${String(error)}`);
    },
  );
}

function sendNative(clip: string): void {
  invokeNative?.("sfx_play", { name: clip }).then(
    (played) => {
      if (played !== true) {
        native = "off";
        note(`native player lost ${clip}`);
      }
    },
    (error: unknown) => {
      native = "off";
      note(`native player off: ${String(error)}`);
    },
  );
}

function playNative(name: Sfx): void {
  // The generic click and an action's own sound fire in the same click; a
  // system sound cannot be stopped once started, so the click waits a tick.
  if (SPECIFIC.has(name) && pendingConfirm !== null) {
    window.clearTimeout(pendingConfirm);
    pendingConfirm = null;
  }
  let take = 0;
  if (name === "done") {
    take = nextDoneIndex();
  } else if (TAKES[name]) {
    take = nextTake.get(name) ?? 0;
    nextTake.set(name, (take + 1) % TAKES[name]);
  }
  const clip = clipName(name, take);
  if (name === "confirm") {
    pendingConfirm = window.setTimeout(() => {
      pendingConfirm = null;
      sendNative(clip);
    }, 0);
    return;
  }
  sendNative(clip);
}

const seen = new Map<string, number>();

/**
 * Play `name` when a tracked number goes up during this app session.
 *
 * The first value seen for a key is only recorded, so loading a page — or
 * moving between two pages that show the same number — is silent.
 */
export function playOnRise(key: string, value: number, name: Sfx): void {
  const previous = seen.get(key);
  seen.set(key, value);
  if (previous !== undefined && value > previous) {
    playSfx(name);
  }
}
